<template>
	<div class="upload-file">
		<el-upload
			v-if="isEditing"
			multiple
			name="fileName"
			:action="uploadFileUrl"
			:before-upload="handleBeforeUpload"
			:file-list="fileList"
			:on-error="handleUploadError"
			:on-success="handleUploadSuccess"
			:show-file-list="false"
			:headers="headers"
			class="upload-file-uploader"
			accept="image/png,image/jpeg,image/jpg,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
			ref="fileUpload"
		>
			<!-- 上传按钮 -->
			<el-button plain type="primary" class="el-button--mini-h"><i class="el-icon-upload"></i>选取文件</el-button>
		</el-upload>
		<!-- 上传提示 -->
		<div class="el-upload__tip" v-if="isEditing && showTip">
			<template v-if="fileSize">
				文件大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>；
			</template>
			<template v-if="fileType">
				格式为 <b style="color: #f56c6c">{{ fileType.join('，') }}</b>
			</template>
			的文件
		</div>
		<!-- 文件列表 -->
		<transition-group class="upload-file-list el-upload-list el-upload-list--text" name="el-fade-in-linear" tag="ul">
			<li class="el-upload-list__item ele-upload-list__item-content" v-for="(file, index) in fileList" :key="index" @click="handlePreview(file.url)">
				<el-link :underline="false" target="_blank">
					<span class="el-icon-document">
						{{ file.name }}
					</span>
				</el-link>
				<div class="ele-upload-list__item-content-action">
					<el-link :underline="false" @click="handlePreview(file.url)" type="primary" style="margin-left: 30px">预览</el-link>
					<el-link v-if="isEditing" :underline="false" @click="handleDelete(index)" type="danger" style="margin-left: 6px">删除</el-link>
				</div>
			</li>
		</transition-group>
	</div>
</template>

<script>
import User from '@/utils/User.js';
import { getFilesInfo } from '@/api/file/file.js';

export default {
	name: 'UploadFileSingle',
	props: {
		modelValue: [String, Object, Array],
		// 数量限制
		limit: {
			type: Number,
			default: 1
		},
		// 大小限制(MB)
		fileSize: {
			type: Number,
			default: 100
		},
		// 文件类型, 例如['png', 'jpg', 'jpeg']
		fileType: {
			type: Array,
			default: () => ['pdf']
		},
		// 是否显示提示
		isShowTip: {
			type: Boolean,
			default: true
		},
		// 文件列表--回显
		editFileList: {
			type: Array,
			default: () => []
		},
		// 是否允许编辑（上传/删除），false 时仅展示预览
		isEditing: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			uploadFileUrl: '/webL_api/third/files/uploadFile', // 上传的文件服务器地址
			uploadParams: {},
			uploadFileList: [],
			headers: { Authorization: 'Bearer ' + User.getToken() },
			fileList: []
		};
	},

	computed: {
		showTip() {
			return this.isShowTip && (this.fileType || this.fileSize);
		}
	},

	watch: {
		modelValue: {
			handler(val) {
				if (val) {
					let temp = 1;
					// 首先将值转为数组
					const list = Array.isArray(val) ? val : this.modelValue.split(',');
					// 然后将数组转为对象数组
					this.fileList = list.map(item => {
						if (typeof item === 'string') {
							item = { name: item, url: item };
						}
						item.uid = item.uid || new Date().getTime() + temp++;
						return item;
					});
				} else {
					this.fileList = [];
					return [];
				}
			},
			deep: true,
			immediate: true
		},

		editFileList: {
			handler(val) {
				if (val === null) {
					this.fileList = [];
				} else {
					if (val.length) {
						this.fileList = val;
					}
				}
			},
			deep: true
		}
	},

	methods: {
		// 上传前校检格式和大小
		handleBeforeUpload(file) {
			// 校检文件类型
			if (this.fileType.length) {
				const fileName = file.name.split('.');
				const fileExt = fileName[fileName.length - 1];
				// 处理带点的文件类型（如 .pdf）
				const fileExtensions = this.fileType.map(type => (type.startsWith('.') ? type.substring(1) : type));
				const isTypeOk = fileExtensions.indexOf(fileExt) >= 0;
				if (!isTypeOk) {
					this.$message.error(`文件格式不正确, 请上传${this.fileType.join('，')}格式文件!`);
					return false;
				}
			}
			// 校检文件大小
			if (this.fileSize) {
				const isLt = file.size / 1024 / 1024 < this.fileSize;
				if (!isLt) {
					this.$message.error(`上传文件大小不能超过 ${this.fileSize} MB!`);
					return false;
				}
			}
			// 数量限制：limit>1 达到上限时阻止；limit=1 由 handleUploadSuccess 替换
			if (this.limit > 1 && this.fileList.length >= this.limit) {
				this.$message.error(`上传文件数量不能超过 ${this.limit} 个!`);
				return false;
			}

			return true;
		},

		// 上传失败
		handleUploadError(err) {
			this.$message.error('上传文件失败');
		},

		// 上传成功回调
		handleUploadSuccess(res, file) {
			console.log('Success', res, file);

			// 如果上传成功
			if (res.code == 200) {
				// console.log('上传成功', res.data);
				let fileUrl = res.data;
				let showFileUrl = fileUrl.replace('/third/files/info/', '');

				// 创建新的文件对象并添加到列表
				const newFile = {
					name: file.name,
					url: fileUrl,
					uid: file.uid
				};

				// 根据 limit 决定添加到列表的方式
				if (this.limit === 1) {
					// 单文件：直接替换
					this.fileList = [newFile];
				} else {
					// 多文件：追加
					this.fileList.push(newFile);
				}

				// console.log('fileList', this.fileList);
				// console.log('fileUrl', this.listToString(this.fileList));

				// 触发更新事件
				this.$emit('update:modelValue', this.listToString(this.fileList));
				this.$emit('change', this.listToString(this.fileList));
				this.$message.success('文件上传成功');
			} else {
				this.$message.error('文件上传失败');
			}
		},

		// 删除文件
		handleDelete(index) {
			// 删除指定索引的文件
			this.fileList.splice(index, 1);
			this.$emit('update:modelValue', this.listToString(this.fileList));
			this.$emit('change', this.listToString(this.fileList));
			this.$message.success('文件删除成功');
		},

		//预览
		async handlePreview(url) {
			try {
				const prefix = '/third/files/info/';
				const key = url.replace(prefix, '');
				const response = await fetch(`/webL_api/third/files/info/${key}`, {
					headers: {
						Authorization: 'Bearer ' + User.getToken()
					}
				});
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				const arrayBuffer = await response.arrayBuffer();
				// 调试：打印前几个字节，确认服务端返回的是文件二进制还是 JSON 包装
				const debugBytes = new Uint8Array(arrayBuffer.slice(0, 16));
				console.log('response contentType:', response.headers.get('Content-Type'));
				console.log('first 16 bytes:', Array.from(debugBytes).map(b => '0x' + b.toString(16).padStart(2, '0')).join(' '));
				// 服务端 Content-Type 不可靠，通过文件头 magic bytes 判断真实类型
				const mimeType = this.detectMimeType(arrayBuffer);
				console.log('mimeType',mimeType)
				const blob = new Blob([arrayBuffer], { type: mimeType });
				const blobUrl = URL.createObjectURL(blob);
				window.open(blobUrl);
			} catch (error) {
				console.error('获取文件失败:', error);
				return null;
			}
		},

		// 通过文件头 magic bytes 判断 MIME 类型
		detectMimeType(buffer) {
			const arr = new Uint8Array(buffer);
			// PDF: %PDF-
			if (arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46) return 'application/pdf';
			// PNG: \x89PNG
			if (arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47) return 'image/png';
			// JPEG: \xFF\xD8\xFF
			if (arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF) return 'image/jpeg';
			// GIF: GIF8
			if (arr[0] === 0x47 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x38) return 'image/gif';
			// BMP: BM
			if (arr[0] === 0x42 && arr[1] === 0x4D) return 'image/bmp';
			// WEBP: RIFF....WEBP
			if (arr[0] === 0x52 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x46 &&
				arr[8] === 0x57 && arr[9] === 0x45 && arr[10] === 0x42 && arr[11] === 0x50) return 'image/webp';
			// DOC (OLE2): D0 CF 11 E0 A1 B1 1A E1
			if (arr[0] === 0xD0 && arr[1] === 0xCF && arr[2] === 0x11 && arr[3] === 0xE0 &&
				arr[4] === 0xA1 && arr[5] === 0xB1 && arr[6] === 0x1A && arr[7] === 0xE1) return 'application/msword';
			// DOCX (Office Open XML, ZIP 格式): PK\x03\x04
			if (arr[0] === 0x50 && arr[1] === 0x4B && arr[2] === 0x03 && arr[3] === 0x04) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
			return 'application/octet-stream';
		},

		// 上传结束处理
		uploadedSuccessfully() {
			// console.log('Successfully', this.fileList);
			if (this.fileList.length >= 2) {
				this.fileList.splice(0, 1);
			}
			this.$emit('update:modelValue', this.listToString(this.fileList));
			this.$modal.closeLoading();
		},

		// 对象转成指定字符串分隔
		listToString(list, separator) {
			let strs = '';
			separator = separator || ',';
			for (let i in list) {
				if (list[i].url) {
					strs += list[i].url + separator;
				}
			}
			return strs != '' ? strs.substr(0, strs.length - 1) : '';
		},

		//获取fileList
		getFileList() {
			return this.fileList;
		},

		//获取fileList
		clearFileList() {
			this.fileList = [];
		}
	}
};
</script>

<style scoped lang="less">
.upload-file-uploader {
	margin-bottom: 10px;
}
.upload-file-list .el-upload-list__item {
	border: 1px solid #e4e7ed;
	line-height: 2;
	margin-bottom: 8px;
	position: relative;
	padding: 8px 12px;
	border-radius: 4px;
	background-color: #f5f7fa;
	transition: all 0.3s ease;

	&:hover {
		background-color: #f0f2f5;
		border-color: #c0c4cc;
	}
}
.upload-file-list .ele-upload-list__item-content {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	color: inherit;
	font-size: 14px;
	gap: 12px;

	> .el-link {
		flex: 1;
		min-width: 0;
		word-break: break-all;
		overflow-wrap: break-word;
		justify-content: normal;
	}
}
.ele-upload-list__item-content-action {
	flex-shrink: 0;
	white-space: nowrap;
}
.ele-upload-list__item-content-action .el-link {
	margin-right: 10px;
	font-size: 13px;
}
.el-upload__tip {
	color: #909399;
	font-size: 12px;
	line-height: 1.5;
	margin-top: 5px;
}
</style>
